import React, { useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Form,
  Modal,
  ToggleButton,
  ToggleButtonGroup,
} from "react-bootstrap";
import { Formik } from "formik";
import * as Yup from "yup";
import axios from "axios";
import brandImage from "../assets/mcollectbrandimage.jpg";
import "../styles/Login.css";
import { useDispatch } from "react-redux";
import { loginSuccess } from "../redux/authSlice/authSlice";
import { toast } from "react-toastify";
import { Baseurl } from "../baseurl";
import { FaEye, FaEyeSlash } from "react-icons/fa";

const LoginSchema = Yup.object().shape({
  userType: Yup.string()
    .oneOf(["agency", "employee"], "Please select a user type")
    .required("User type is required"),
  email: Yup.string()
    .email("Invalid email address")
    .required("Email is required"),
  password: Yup.string()
    .min(6, "Password must be at least 6 characters")
    .required("Password is required"),
});

const ForgotPasswordSchema = Yup.object().shape({
  userType: Yup.string()
    .oneOf(["agency", "employee"], "Please select a user type")
    .required("User type is required"),
  mobileNo: Yup.string()
    .matches(/^[0-9]{10}$/, "Mobile number must be 10 digits")
    .required("Mobile number is required"),
});

const ResetPasswordSchema = Yup.object().shape({
  userType: Yup.string()
    .oneOf(["agency", "employee"], "Please select a user type")
    .required("User type is required"),
  otp: Yup.string()
    .matches(/^[0-9]{6}$/, "OTP must be 6 digits")
    .required("OTP is required"),
  newPassword: Yup.string()
    .min(6, "Password must be at least 6 characters")
    .required("New password is required"),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("newPassword"), null], "Passwords must match")
    .required("Confirm password is required"),
});

const Login = () => {
  const dispatch = useDispatch();
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [mobileNo, setMobileNo] = useState("");
  const [userType, setUserType] = useState("");
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);

  const handleLogin = async (values, { setSubmitting }) => {
    try {
      const config = {
        headers: {
          "Content-Type": "application/json",
        },
      };

      let apiEndpoint = "";

      // Determine API endpoint based on user type
      if (values.userType === "agency") {
        apiEndpoint = `${Baseurl}/api/agency/login`;
      } else if (values.userType === "employee") {
        apiEndpoint = `${Baseurl}/api/fieldOfficer/login`;
      } else {
        toast.error("Please select a valid user type");
        return;
      }

      const resp = await axios.post(apiEndpoint, values, config);

      if (resp.data?.success) {
        toast.success("Login successful");

        // Store user type in the response data
        const userData = {
          ...(resp.data[values.userType] ||
            resp.data.agency ||
            resp.data.loginData),
          userType: values.userType,
        };

        await dispatch(loginSuccess(userData));
      } else {
        toast.error(resp.data?.message || "Login failed");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Invalid credentials");
    } finally {
      setSubmitting(false);
    }
  };

  const handleForgotPassword = async (values, { setSubmitting }) => {
    try {
      const config = {
        headers: {
          "Content-Type": "application/json",
        },
      };

      let apiEndpoint = "";

      // Determine API endpoint based on user type
      if (values.userType === "agency") {
        apiEndpoint = `${Baseurl}/api/agency/send-reset-otp`;
      } else if (values.userType === "employee") {
        apiEndpoint = `${Baseurl}/api/fieldOfficer/send-reset-otp`;
      } else {
        toast.error("Please select a valid user type");
        return;
      }

      const resp = await axios.post(
        apiEndpoint,
        { mobileNo: values.mobileNo },
        config,
      );

      if (resp.data?.success) {
        toast.success("OTP sent to your registered email");
        setMobileNo(values.mobileNo);
        setUserType(values.userType);
        setIsOtpSent(true);
        setShowForgotPassword(false);
        setShowResetPassword(true);
      } else {
        toast.error(resp.data?.message || "Failed to send OTP");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to send OTP");
    } finally {
      setSubmitting(false);
    }
  };

  const handleResetPassword = async (values, { setSubmitting }) => {
    try {
      const config = {
        headers: {
          "Content-Type": "application/json",
        },
      };

      let apiEndpoint = "";

      // Determine API endpoint based on user type
      if (values.userType === "agency") {
        apiEndpoint = `${Baseurl}/api/agency/reset-password`;
      } else if (values.userType === "employee") {
        apiEndpoint = `${Baseurl}/api/fieldOfficer/reset-password`;
      } else {
        toast.error("Please select a valid user type");
        return;
      }

      const requestData = {
        mobileNo: mobileNo,
        otp: values.otp,
        newPassword: values.newPassword,
      };

      const resp = await axios.post(apiEndpoint, requestData, config);

      if (resp.data?.success) {
        toast.success("Password reset successful! Logging you in...");
        setIsResettingPassword(true);

        // Determine login endpoint based on user type
        let loginEndpoint = "";
        if (values.userType === "agency") {
          loginEndpoint = `${Baseurl}/api/agency/login`;
        } else if (values.userType === "employee") {
          loginEndpoint = `${Baseurl}/api/fieldOfficer/login`;
        }

        // Auto login after reset
        const loginResp = await axios.post(
          loginEndpoint,
          {
            email:
              resp.data[values.userType]?.email ||
              resp.data.agency?.email ||
              resp.data.data?.email,
            password: values.newPassword,
            userType: values.userType,
          },
          config,
        );

        if (loginResp.data?.success) {
          // Store user type in the response data
          const userData = {
            ...(loginResp.data[values.userType] ||
              loginResp.data.agency ||
              loginResp.data.loginData),
            userType: values.userType,
          };

          await dispatch(loginSuccess(userData));
          toast.success("Login successful");
          setShowResetPassword(false);
        }
      } else {
        toast.error(resp.data?.message || "Failed to reset password");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to reset password");
    } finally {
      setSubmitting(false);
      setIsResettingPassword(false);
    }
  };

  return (
    <Container fluid className="login-page">
      <Row className="justify-content-center align-items-center vh-100">
        <Col md={6} lg={4}>
          <Card className="login-card shadow">
            <Card.Body>
              {/* Brand Section */}
              <div className="text-center mb-4">
                <img
                  src={brandImage}
                  alt="mCollect"
                  className="login-brand-image"
                />
                <h4 className="mt-3">mCollect Admin Panel</h4>
                <p className="text-muted tagline">
                  Smart Auctions. Secure Management.
                </p>
              </div>

              {/* Login Form */}
              <Formik
                initialValues={{ userType: "", email: "", password: "" }}
                validationSchema={LoginSchema}
                onSubmit={handleLogin}
              >
                {({
                  handleSubmit,
                  handleChange,
                  setFieldValue,
                  values,
                  touched,
                  errors,
                  isSubmitting,
                }) => (
                  <Form noValidate onSubmit={handleSubmit}>
                    <Form.Group className="mb-3">
                      <Form.Label>Login As</Form.Label>
                      <div>
                        <ToggleButtonGroup
                          type="radio"
                          name="userType"
                          value={values.userType}
                          onChange={(value) => setFieldValue("userType", value)}
                          className="w-100"
                        >
                          <ToggleButton
                            id="agency-btn"
                            value="agency"
                            variant={
                              values.userType === "agency"
                                ? "primary"
                                : "outline-primary"
                            }
                            className="w-50"
                          >
                            Agency
                          </ToggleButton>
                          <ToggleButton
                            id="employee-btn"
                            value="employee"
                            variant={
                              values.userType === "employee"
                                ? "primary"
                                : "outline-primary"
                            }
                            className="w-50"
                          >
                            Employee
                          </ToggleButton>
                        </ToggleButtonGroup>
                        {touched.userType && errors.userType && (
                          <div className="text-danger small mt-1">
                            {errors.userType}
                          </div>
                        )}
                      </div>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Email Address</Form.Label>
                      <Form.Control
                        type="email"
                        name="email"
                        placeholder={
                          values.userType === "agency"
                            ? "agency@example.com"
                            : "employee@example.com"
                        }
                        value={values.email}
                        onChange={handleChange}
                        isInvalid={touched.email && !!errors.email}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.email}
                      </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Password</Form.Label>
                      <div className="position-relative">
                        <Form.Control
                          type={showPassword ? "text" : "password"}
                          name="password"
                          placeholder="Enter password"
                          value={values.password}
                          onChange={handleChange}
                          isInvalid={touched.password && !!errors.password}
                        />
                        <Button
                          variant="link"
                          className="position-absolute top-50 end-0 translate-middle-y"
                          style={{ right: "10px" }}
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? <FaEyeSlash /> : <FaEye />}
                        </Button>
                      </div>
                      <Form.Control.Feedback type="invalid">
                        {errors.password}
                      </Form.Control.Feedback>
                    </Form.Group>

                    <div className="d-flex justify-content-end mb-3">
                      <Button
                        variant="link"
                        className="p-0 text-decoration-none"
                        style={{ color: "#304356" }}
                        onClick={() => setShowForgotPassword(true)}
                      >
                        Forgot Password?
                      </Button>
                    </div>

                    <Button
                      type="submit"
                      style={{ background: "#304356", borderColor: "#304356" }}
                      className="w-100"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? "Logging in..." : "Login"}
                    </Button>
                  </Form>
                )}
              </Formik>
            </Card.Body>
          </Card>

          <p className="text-center mt-3 text-white footer-text">
            © {new Date().getFullYear()} mCollect. All rights reserved.
          </p>
        </Col>
      </Row>

      {/* Forgot Password Modal */}
      <Modal
        show={showForgotPassword}
        onHide={() => setShowForgotPassword(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Forgot Password</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Formik
            initialValues={{ userType: "", mobileNo: "" }}
            validationSchema={ForgotPasswordSchema}
            onSubmit={handleForgotPassword}
          >
            {({
              handleSubmit,
              handleChange,
              setFieldValue,
              values,
              touched,
              errors,
              isSubmitting,
            }) => (
              <Form noValidate onSubmit={handleSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label>User Type</Form.Label>
                  <div>
                    <ToggleButtonGroup
                      type="radio"
                      name="userType"
                      value={values.userType}
                      onChange={(value) => setFieldValue("userType", value)}
                      className="w-100"
                    >
                      <ToggleButton
                        id="forgot-agency-btn"
                        value="agency"
                        variant={
                          values.userType === "agency"
                            ? "primary"
                            : "outline-primary"
                        }
                        className="w-50"
                      >
                        Admin
                      </ToggleButton>
                      <ToggleButton
                        id="forgot-employee-btn"
                        value="employee"
                        variant={
                          values.userType === "employee"
                            ? "primary"
                            : "outline-primary"
                        }
                        className="w-50"
                      >
                        Employee
                      </ToggleButton>
                    </ToggleButtonGroup>
                    {touched.userType && errors.userType && (
                      <div className="text-danger small mt-1">
                        {errors.userType}
                      </div>
                    )}
                  </div>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Mobile Number</Form.Label>
                  <Form.Control
                    type="tel"
                    name="mobileNo"
                    placeholder="Enter your registered mobile number"
                    value={values.mobileNo}
                    onChange={handleChange}
                    isInvalid={touched.mobileNo && !!errors.mobileNo}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.mobileNo}
                  </Form.Control.Feedback>
                  <Form.Text className="text-muted">
                    OTP will be sent to your registered email address
                  </Form.Text>
                </Form.Group>

                <Button
                  type="submit"
                  style={{ background: "#304356", borderColor: "#304356" }}
                  className="w-100"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Sending OTP..." : "Send OTP"}
                </Button>
              </Form>
            )}
          </Formik>
        </Modal.Body>
      </Modal>

      {/* Reset Password Modal */}
      <Modal
        show={showResetPassword}
        onHide={() => setShowResetPassword(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Reset Password</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <p className="text-muted">
              User Type:{" "}
              <strong>{userType === "agency" ? "Agency" : "Employee"}</strong>
            </p>
            <p className="text-muted">
              Mobile Number: <strong>{mobileNo}</strong>
            </p>
          </div>

          <Formik
            initialValues={{
              userType: userType,
              otp: "",
              newPassword: "",
              confirmPassword: "",
            }}
            validationSchema={ResetPasswordSchema}
            onSubmit={handleResetPassword}
          >
            {({
              handleSubmit,
              handleChange,
              setFieldValue,
              values,
              touched,
              errors,
              isSubmitting,
            }) => (
              <Form noValidate onSubmit={handleSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label>User Type</Form.Label>
                  <div>
                    <ToggleButtonGroup
                      type="radio"
                      name="userType"
                      value={values.userType}
                      onChange={(value) => setFieldValue("userType", value)}
                      className="w-100"
                      disabled={true}
                    >
                      <ToggleButton
                        id="reset-agency-btn"
                        value="agency"
                        variant="primary"
                        className="w-50"
                      >
                        Admin
                      </ToggleButton>
                      <ToggleButton
                        id="reset-employee-btn"
                        value="employee"
                        variant="primary"
                        className="w-50"
                      >
                        Employee
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </div>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>OTP</Form.Label>
                  <Form.Control
                    type="text"
                    name="otp"
                    placeholder="Enter 6-digit OTP"
                    value={values.otp}
                    onChange={handleChange}
                    isInvalid={touched.otp && !!errors.otp}
                    maxLength="6"
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.otp}
                  </Form.Control.Feedback>
                  <Form.Text className="text-muted">
                    Check your email for OTP
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>New Password</Form.Label>
                  <Form.Control
                    type="password"
                    name="newPassword"
                    placeholder="Enter new password"
                    value={values.newPassword}
                    onChange={handleChange}
                    isInvalid={touched.newPassword && !!errors.newPassword}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.newPassword}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Confirm Password</Form.Label>
                  <Form.Control
                    type="password"
                    name="confirmPassword"
                    placeholder="Confirm new password"
                    value={values.confirmPassword}
                    onChange={handleChange}
                    isInvalid={
                      touched.confirmPassword && !!errors.confirmPassword
                    }
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.confirmPassword}
                  </Form.Control.Feedback>
                </Form.Group>

                <Button
                  type="submit"
                  style={{ background: "#304356", borderColor: "#304356" }}
                  className="w-100"
                  disabled={isSubmitting || isResettingPassword}
                >
                  {isResettingPassword
                    ? "Logging in..."
                    : isSubmitting
                      ? "Resetting..."
                      : "Reset Password & Login"}
                </Button>
              </Form>
            )}
          </Formik>
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default Login;
